import coverLayout from '../config/streamerCoverLayout.json';

export const STREAMER_IG_USERNAME = 'streameruniversity';

export const STREAMER_HASHTAGS = [
  '#UniStreaming',
  '#Streamer',
  '#ContentCreator',
  '#Twitch',
  '#Kick',
  '#TikTok'
];

export const PLATFORM_META = {
  twitch: { label: 'Twitch', color: '#9146FF' },
  instagram: { label: 'Instagram', color: '#E4405F' },
  kick: { label: 'Kick', color: '#53FC18' },
  youtube: { label: 'YouTube', color: '#FF0000' },
  tiktok: { label: 'TikTok', color: '#010101' }
};

export const NAME_FONT_FAMILY = coverLayout.nameFontFamily;
export const HANDLE_FONT_FAMILY = coverLayout.handleFontFamily;

// Served from apps/frontend/public/logos/ — bundled with the frontend deploy (no backend needed).
export const getPlatformLogoSrc = (platform) => `/logos/${platform}.png`;

export const loadPlatformLogoImage = (platform) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = getPlatformLogoSrc(platform);
  });

export const loadPreviewFonts = async () => {
  const nameSize = coverLayout.name.fontSize;
  const handleSize = coverLayout.platforms.handleFontSize;
  await Promise.all([
    document.fonts.load(`400 ${nameSize}px "${NAME_FONT_FAMILY}"`),
    document.fonts.load(`700 ${handleSize}px "${HANDLE_FONT_FAMILY}"`),
    document.fonts.load(`900 ${handleSize}px "${HANDLE_FONT_FAMILY}"`)
  ]);
  await document.fonts.ready;
};

export const formatStreamerPlatformHandle = (platform, handle) => {
  if (!handle) return '';
  const stripped = handle.replace(/^@/, '');
  return platform === 'instagram' ? `@${stripped}` : stripped;
};

export const buildStreamerCaption = (submissionData) => {
  const { bio, platforms } = submissionData;
  let caption = `${bio || ''}`;

  (platforms || []).forEach(({ platform, handle }) => {
    if (!handle) return;
    const label = PLATFORM_META[platform]?.label || platform;
    caption += `\n${label}: ${formatStreamerPlatformHandle(platform, handle)}`;
  });

  caption += `\n\n${STREAMER_HASHTAGS.join(' ')}\n\n`;
  caption += 'Want to be featured? Click the link in our bio!';
  return caption;
};

export const drawCoverPreview = ({
  ctx,
  size,
  coverImg,
  templateImage,
  submissionData,
  logoImages
}) => {
  ctx.drawImage(coverImg, 0, 0, size, size);
  ctx.drawImage(templateImage, 0, 0, size, size);

  const scale = size / 1080;
  const nameCfg = coverLayout.name;
  const platCfg = coverLayout.platforms;

  const firstName = submissionData.name.split(' ')[0].toUpperCase();
  const nameFontFamily = nameCfg.fontFamily || NAME_FONT_FAMILY;
  const nameFontSize = Math.round(nameCfg.fontSize * scale);

  ctx.font = `400 ${nameFontSize}px "${nameFontFamily}", Impact, sans-serif`;
  ctx.fillStyle = nameCfg.color;
  ctx.textAlign = nameCfg.align || 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = nameCfg.shadowColor;
  ctx.shadowBlur = nameCfg.shadowBlur * scale;
  ctx.shadowOffsetX = nameCfg.shadowOffsetX * scale;
  ctx.shadowOffsetY = nameCfg.shadowOffsetY * scale;

  const nameX = nameCfg.x * scale;
  const nameY = nameCfg.y * scale;
  const rotation = ((nameCfg.rotation || 0) * Math.PI) / 180;

  ctx.save();
  ctx.translate(nameX, nameY);
  ctx.rotate(rotation);
  ctx.fillText(firstName, 0, 0);
  ctx.restore();
  ctx.shadowColor = 'transparent';

  const platforms = (submissionData.platforms || []).filter((p) => p?.handle);
  platforms.forEach(({ platform, handle }, i) => {
    const logoStartX = platCfg.logoStartX[i];
    if (logoStartX === undefined) return;

    const logoSize = platCfg.logoSize * scale;
    const handleFontSize = Math.round(platCfg.handleFontSize * scale);
    const handleText = handle.startsWith('@') ? handle : `@${handle}`;
    const gap = platCfg.logoHandleGap * scale;
    const rowY = platCfg.y * scale - logoSize / 2;

    const logoX = logoStartX * scale;
    const textX = logoX + logoSize + gap;

    const logo = logoImages[platform];
    if (logo) {
      ctx.drawImage(logo, logoX, rowY, logoSize, logoSize);
    } else {
      ctx.fillStyle = PLATFORM_META[platform]?.color || '#333333';
      ctx.beginPath();
      ctx.arc(logoX + logoSize / 2, rowY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.fillStyle = platCfg.color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = `700 ${handleFontSize}px "${HANDLE_FONT_FAMILY}", Arial, sans-serif`;
    ctx.fillText(handleText, textX, rowY + logoSize / 2);
  });
};
